import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Cobranza, CobranzaCreateRequest, EstadoCuentaCliente } from '../types/cobranza.types';
import { CobranzaService } from '../services/cobranzaService';

interface CobranzaState {
  cobranzas: Cobranza[];
  selectedCobranza: Cobranza | null;
  estadoCuenta: EstadoCuentaCliente | null;
  loading: boolean;
  error: string | null;
  dialogOpen: boolean;

  fetchCobranzas: () => Promise<void>;
  fetchCobranzaById: (id: number) => Promise<void>;
  saveCobranza: (data: CobranzaCreateRequest) => Promise<void>;
  deleteCobranza: (id: number) => Promise<void>;
  fetchEstadoCuenta: (idCliente: number) => Promise<void>;
  setSelectedCobranza: (cobranza: Cobranza | null) => void;
  openDialog: () => void;
  closeDialog: () => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  cobranzas: [],
  selectedCobranza: null,
  estadoCuenta: null,
  loading: false,
  error: null,
  dialogOpen: false,
};

export const useCobranzaStore = create<CobranzaState>()(
  devtools(
    (set) => ({
      ...initialState,

      fetchCobranzas: async () => {
        set({ loading: true, error: null }, false, 'cobranza/fetchAll/start');
        try {
          const cobranzas = await CobranzaService.getAll();
          set({ cobranzas, loading: false }, false, 'cobranza/fetchAll/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar cobranzas' }, false, 'cobranza/fetchAll/error');
        }
      },

      fetchCobranzaById: async (id: number) => {
        set({ loading: true, error: null }, false, 'cobranza/fetchById/start');
        try {
          const cobranza = await CobranzaService.getById(id);
          set({ selectedCobranza: cobranza, loading: false }, false, 'cobranza/fetchById/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar cobranza' }, false, 'cobranza/fetchById/error');
        }
      },

      saveCobranza: async (data: CobranzaCreateRequest) => {
        set({ loading: true, error: null }, false, 'cobranza/save/start');
        try {
          await CobranzaService.save(data);
          set({ loading: false, dialogOpen: false }, false, 'cobranza/save/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al guardar cobranza' }, false, 'cobranza/save/error');
          throw error;
        }
      },

      deleteCobranza: async (id: number) => {
        set({ loading: true, error: null }, false, 'cobranza/delete/start');
        try {
          await CobranzaService.delete(id);
          set((state) => ({
            cobranzas: state.cobranzas.filter((c) => c.idCobranza !== id),
            loading: false,
          }), false, 'cobranza/delete/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al eliminar cobranza' }, false, 'cobranza/delete/error');
        }
      },

      fetchEstadoCuenta: async (idCliente: number) => {
        set({ loading: true, error: null }, false, 'cobranza/estadoCuenta/start');
        try {
          const estadoCuenta = await CobranzaService.getEstadoCuenta(idCliente);
          set({ estadoCuenta, loading: false }, false, 'cobranza/estadoCuenta/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar estado de cuenta' }, false, 'cobranza/estadoCuenta/error');
        }
      },

      setSelectedCobranza: (cobranza) => set({ selectedCobranza: cobranza }, false, 'cobranza/setSelected'),
      openDialog: () => set({ dialogOpen: true }, false, 'cobranza/openDialog'),
      closeDialog: () => set({ dialogOpen: false, selectedCobranza: null }, false, 'cobranza/closeDialog'),
      clearError: () => set({ error: null }, false, 'cobranza/clearError'),
      reset: () => set(initialState, false, 'cobranza/reset'),
    }),
    { name: 'cobranza-store' }
  )
);
